// Navbar.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../api'; // 根据你的实际路径调整

const MOBILE_BREAKPOINT = 768;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isProfileHovered, setIsProfileHovered] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const handleResize = () => {
    setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    if (window.innerWidth > MOBILE_BREAKPOINT) setIsOpen(false);
  };

  // 检查未读消息
  const checkUnreadMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/api/media/messages/unread-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const data = response.data.data;
        const hasUnread = data.comments > 0 || data.likes > 0 || data.system > 0;
        setHasUnreadMessages(hasUnread);
      }
    } catch (error) {
      console.error('检查未读消息失败:', error);
    }
  };

  const isTokenValid = () => {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch (err) {
      return false;
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
  
    const valid = isTokenValid();
    setIsLoggedIn(valid);
    
    // 如果用户已登录，检查未读消息
    if (valid) {
      checkUnreadMessages();
    }
  
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { path: '/', label: '首页' },
    { path: '/programs', label: '项目介绍' },
    { path: '/news', label: '新闻资讯' },
    { path: '/', label: '广播节目' },
    { path: '/education-resources', label: '教育资源' },
    { path: '/public-welfare-store', label: '公益店铺' },
    { path: '/join-us', label: '加入我们' },
    ...(isLoggedIn
      ? [] // If logged in, no need to add login or register
      : [
          { path: '/register', label: '注册/登录' }, // Show '注册/登录' if not logged in
        ]),
  ];

  const NavItem = ({ path, label, onClick, showBadge = false }) => {
    const isActive = location.pathname === path;

    return (
      <li style={{ ...styles.navItem }}>
        <Link
          to={path}
          style={{
            ...styles.navLink,
            ...(isActive ? styles.activeNavLink : {}),
            position: 'relative',
          }}
          onClick={onClick}
        >
          {label}
          {showBadge && hasUnreadMessages && (
            <span style={styles.badge}></span>
          )}
        </Link>
      </li>
    );
  };

  return (
    <nav style={styles.navbar}>
      <div style={styles.secondLayer}>
        <div style={styles.navWrapper}>
          {/* 三明治按钮（靠左） */}
          {isMobile && (
            <button style={styles.hamburger} onClick={toggleMenu}>
              ☰
            </button>
          )}

          {/* 桌面端导航菜单 */}
          {!isMobile && (
            <ul style={styles.navList}>
              {navItems.map((item) => (
                <NavItem key={item.path} {...item} />
              ))}
            </ul>
          )}

          {/* 个人资料按钮（靠右） */}
          {isLoggedIn && (
            <div
              style={styles.profileContainer}
              onMouseEnter={() => setIsProfileHovered(true)}
              onMouseLeave={() => setIsProfileHovered(false)}
            >
              <div style={styles.profileButton}>
                个人中心
                {hasUnreadMessages && (
                  <span style={styles.profileBadge}></span>
                )}
              </div>
              {isProfileHovered && (
                <div style={styles.profileDropdown}>
                  <ul style={styles.profileDropdownList}>
                    <NavItem path="/profile-upload" label="个人资料" />
                    <NavItem path="/my-audios" label="我的音频" />
                    <NavItem path="/my-audio-collections" label="我的合集" />
                    <NavItem 
                      path="/messages/comments" 
                      label="消息中心" 
                      showBadge={true}
                    />
                    <NavItem path="/manual" label="HiRadio使用手册" />
                    <NavItem path="/logout" label="登出" />
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 移动端浮动菜单 */}
        {isMobile && isOpen && (
          <div style={styles.mobileMenu}>
            <ul style={styles.mobileMenuList}>
              {navItems.map((item) => (
                <NavItem key={item.path} {...item} onClick={closeMenu} />
              ))}
              {isLoggedIn && (
                <NavItem 
                  path="/messages/comments" 
                  label="消息中心" 
                  onClick={closeMenu}
                  showBadge={true}
                />
              )}
            </ul>
          </div>
        )}
      </div>
    </nav>
  );
};

const styles = {
  navbar: {
    backgroundColor: 'white',
    color: 'black',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
  },
  navWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    position: 'relative',
  },
  hamburger: {
    fontSize: '24px',
    background: 'none',
    border: 'none',
    color: 'black',
    cursor: 'pointer',
  },
  mobileMenu: {
    position: 'absolute',
    top: '60px',
    left: '10px',
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    zIndex: 1002,
    padding: '5px 10px',
    borderRadius: '8px',
    width: 'fit-content',
    minWidth: '140px',
    maxWidth: '80vw',
  },
  mobileMenuList: {
    listStyleType: 'none',
    margin: 0,
    padding: '10px 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  navItem: {
    margin: '0 10px',
    position: 'relative',
  },
  navLink: {
    color: 'black',
    textDecoration: 'none',
    fontSize: '16px',
    padding: '5px 12px',
    display: 'block',
    position: 'relative',
  },
  secondLayer: {
    backgroundColor: 'white',
    padding: '5px 10px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
  },
  navList: {
    listStyleType: 'none',
    marginLeft: '120px',
    padding: 0,
    display: 'flex',
    flexGrow: 1,
  },
  profileContainer: {
    position: 'relative',
    marginLeft: 'auto',
  },
  profileButton: {
    padding: '8px 15px',
    backgroundColor: '#f0f0f0',
    borderRadius: '5px',
    transition: 'background-color 0.3s ease',
    position: 'relative',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
  profileDropdown: {
    position: 'absolute',
    top: '35px',
    right: '0',
    backgroundColor: '#fff',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    borderRadius: '5px',
    width: '170px',
    opacity: 1,
    transition: 'opacity 0.3s ease-in-out',
    zIndex: 1001,
  },
  profileDropdownList: {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
  },
  activeNavLink: {
    color: '#1890ff',
    fontWeight: 'bold',
    borderBottom: '2px solid #1890ff',
  },
  // 红点样式
  badge: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    width: '8px',
    height: '8px',
    backgroundColor: '#ff4d4f',
    borderRadius: '50%',
  },
  profileBadge: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    width: '8px',
    height: '8px',
    backgroundColor: '#ff4d4f',
    borderRadius: '50%',
  },
};

export default Navbar;